import React, { Key, useRef } from 'react';
import styles from './styles.module.scss';
import { InfoField } from '@components-clean/core/info-field';
import { useElementSize } from '@hooks-clean/index';
import { NullableStudy } from '@contexts/local-state-context';
import { useTranslation } from '@hooks/index';
import { requireStudy } from '@config/routes/page-data-helpers';

type Props = {
    routePath: string | undefined;
    study: NullableStudy | undefined;
    children?: React.ReactNode;
};

// *** Make sure to keep this synced with the legacy counterpart (./src/views/components/core/page-banners-wrapper/) ***
/**
 * Adds 0 or more banners to the top of the page content based on conditions, such as:
 * - read-only study banner (if selected study is read-only)
 *
 * Sets a CSS property that tracks the height of the page banners container (`--page-banners-height`),
 *   so this can be accounted for by child layouts
 */
const PageBannersWrapper = ({ routePath, study, children }: Props) => {
    const { t } = useTranslation();

    // Watch height of banner (if included) and track its value (or 0) in a CSS variable
    const bannersContainerRef = useRef<HTMLDivElement>(null);
    // Ignore width so it doesn't rerender when only width changes
    const bannersContainerSize = useElementSize(bannersContainerRef, { trackWidth: false });

    // CSS variables are valid inline, but are missing on React.CSSProperties...
    const style = {
        '--page-banners-height': `${bannersContainerSize.height ?? 0}px`,
    } as React.CSSProperties;

    const routeInvolvesStudy = routePath != null && requireStudy(routePath);

    type PageBannerDefinition = {
        key: Key;
        type: 'Info' | 'Warning' | 'Error';
        message: string;
    };
    const banners: PageBannerDefinition[] = [];

    // If path involves study, and current study is read-only, show banner
    if (routeInvolvesStudy && study?.studyReadOnly === true) {
        banners.push({
            key: 'study-read-only',
            type: 'Info',
            message: t('study:readOnlyBanner'),
        });
    }

    return (
        <div style={style}>
            {/* Add banners if any should be shown */}
            {banners.length > 0 && (
                <div ref={bannersContainerRef} className={styles.pageBannersContainer}>
                    {banners.map((b) => (
                        <InfoField key={b.key} type={b.type} message={b.message} />
                    ))}
                </div>
            )}

            {children}
        </div>
    );
};

export default PageBannersWrapper;
